import { useCallback } from 'react';
import type { SkipToContentProps } from './types';

const DEFAULT_LINKS = [{ label: 'Skip to content', targetId: 'main-content' }];

/**
 * WCAG 2.4.1 Bypass Blocks — skip navigation links.
 * Must be the first focusable element in DOM order.
 * Hidden until keyboard-focused (Tab key), visible via :focus-within.
 */
export function SkipToContent({ links }: SkipToContentProps) {
  const resolvedLinks = links && links.length > 0 ? links : DEFAULT_LINKS;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
      e.preventDefault();
      const target = document.getElementById(targetId);
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Ensure the target can receive focus even if it's not natively focusable
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });
    },
    [],
  );

  return (
    <div
      className={[
        // Hidden by default (sr-only equivalent via clip)
        'fixed left-3 top-3 z-max flex gap-2',
        'pointer-events-none opacity-0',
        // Reveal all links when any child receives keyboard focus
        'focus-within:pointer-events-auto focus-within:opacity-100',
        // Transition for appear (reduced motion handled by base.css global rule)
        'transition-[opacity,transform] duration-200 ease-out',
        '-translate-y-2 focus-within:translate-y-0',
      ].join(' ')}
    >
      {resolvedLinks.map((link) => (
        <a
          key={link.targetId}
          href={`#${link.targetId}`}
          onClick={(e) => handleClick(e, link.targetId)}
          className={[
            // Base: completely hidden from visual layout via clip
            'sr-only',
            // Visible on keyboard focus only (:focus-visible, not :focus)
            'focus-visible:not-sr-only',
            'focus-visible:relative focus-visible:block',
            'focus-visible:rounded-md focus-visible:bg-brand-600',
            'focus-visible:px-4 focus-visible:py-2.5',
            'focus-visible:text-sm focus-visible:font-semibold focus-visible:text-white',
            'focus-visible:shadow-lg',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-white focus-visible:ring-offset-2',
            'focus-visible:ring-offset-brand-600',
            // No transition on the link itself — container handles it
            'whitespace-nowrap',
          ].join(' ')}
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}
